import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { InputField } from '@/components/common';
import { FsCategoryService } from '@/services/accountService';
import { usePageCrud } from '@/hooks/usePageCrud';

interface Props {
  open: boolean;
  onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
  categoryId?: number;
}

const FsCategoryDetail: React.FC<Props> = ({ open, onClose, categoryId }) => {
  const isEdit = !!categoryId;
  const { canCreate, canUpdate } = usePageCrud({
    read: ['fs_category_get', 'fs_category_list'],
    create: 'account:fs_category:create',
    update: 'account:fs_category:update',
    remove: 'account:fs_category:delete',
  });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FsCategoryService.FsCategory>({ code: '', nameEn: '', nameZh: '', statement: '', parentId: null, isCalc: 0, calcKey: '', isLinkAfs: 1, isActive: 1, isDel: 0 });
  const [fieldErrors, setFieldErrors] = useState<{ code?: string; statement?: string; nameEn?: string; nameZh?: string; parentId?: string; _global?: string }>({});
  const [parentOptions, setParentOptions] = useState<Record<string, string>>({});

  const loadDetail = async () => {
    if (!categoryId) return;
    try { setForm(await FsCategoryService.getCategory(categoryId)); } catch {}
  };

  useEffect(() => {
    if (open) {
      if (isEdit) loadDetail(); else setForm({ code: '', nameEn: '', nameZh: '', statement: '', parentId: null, isCalc: 0, calcKey: '', isLinkAfs: 1, isActive: 1, isDel: 0 });
      setFieldErrors({});
      FsCategoryService.listAll()
        .then((res: FsCategoryService.FsCategory[]) => {
          const opts: Record<string, string> = {};
          if (Array.isArray(res)) {
            res.forEach((item) => {
              if (item.fsId != null) opts[String(item.fsId)] = item.nameEn || item.code || '';
            });
          }
          setParentOptions(opts);
        })
        .catch(() => setParentOptions({}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryId]);

  const onSave = async () => {
    setSaving(true);
    try {
      if (isEdit && form.fsId) {
        await FsCategoryService.updateCategory(form.fsId, form);
      } else {
        await FsCategoryService.createCategory(form);
      }
      onClose({ refreshed: true, message: 'Saved successfully', severity: 'success' });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      const fe: typeof fieldErrors = { _global: msg };
      const lower = String(msg).toLowerCase();
      if (lower.includes('code')) fe.code = msg;
      if (lower.includes('statement')) fe.statement = msg;
      if (lower.includes('parent')) fe.parentId = msg;
      setFieldErrors(fe);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {isEdit ? 'Edit FS Category' : 'Create FS Category'}
          <IconButton size="small" onClick={() => onClose()} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <InputField
            size="small"
            label="Code"
            required
            error={!form.code || !!fieldErrors.code}
            helperText={fieldErrors.code || (!form.code ? 'Code is required' : '')}
            value={form.code || ''}
            onChange={e => setForm({ ...form, code: e.target.value })}
          />

          <InputField
            size="small"
            label="Parent"
            select
            error={!!fieldErrors.parentId}
            helperText={fieldErrors.parentId || ''}
            value={form.parentId != null ? String(form.parentId) : ''}
            onChange={e => setForm({ ...form, parentId: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <MenuItem value="">None</MenuItem>
            {Object.entries(parentOptions).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </InputField>

          <InputField size="small" label="Name (EN)" value={form.nameEn || ''} onChange={e => setForm({ ...form, nameEn: e.target.value })} />
          <InputField size="small" label="Name (ZH)" value={form.nameZh || ''} onChange={e => setForm({ ...form, nameZh: e.target.value })} />
          <InputField size="small" label="Statement" value={form.statement || ''} onChange={e => setForm({ ...form, statement: e.target.value })} />
          <InputField size="small" label="Schedule" value={form.scheduleName || ''} onChange={e => setForm({ ...form, scheduleName: e.target.value })} />

          <Grid container spacing={2}>
            <Grid size={4}>
              <FormControlLabel
                control={<Checkbox size="small" checked={(form.isHeader ?? 0) === 1} onChange={(e) => setForm({ ...form, isHeader: e.target.checked ? 1 : 0 })} />}
                label="Is Header"
              />
            </Grid>
            <Grid size={4}>
              <FormControlLabel
                control={<Checkbox size="small" checked={(form.isLinkAfs ?? 0) === 1} onChange={(e) => setForm({ ...form, isLinkAfs: e.target.checked ? 1 : 0 })} />}
                label="Link AFS"
              />
            </Grid>
            <Grid size={4}>
              <FormControlLabel
                control={<Checkbox size="small" checked={(form.isActive ?? 0) === 1} onChange={(e) => setForm({ ...form, isActive: e.target.checked ? 1 : 0 })} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        {(isEdit ? canUpdate : canCreate) && (
        <Button variant="contained" onClick={onSave} disabled={saving || !form.code}>Save</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FsCategoryDetail;
